import { ActionIcon, Button, Table } from "@mantine/core"
import { Fragment, useCallback, useEffect, useState } from "react"
import { AuthenticatedAppShell } from "../../components/authenticated-app-shell"
import {
  AppCard,
  DataTableShell,
  EmptyState,
  PageToolbar,
  SectionHeader,
} from "../../components/ui"
import {
  apiDelete,
  apiGet,
  apiPost,
  type ProviderAccountRecord,
  type ProviderConnectionSummary,
} from "../../lib/api"
import { formatLocalDateTime } from "../../lib/dates"
import { formatAccountType, labelForProvider } from "../../lib/provider-labels"

export function AccountsPage() {
  const [connections, setConnections] = useState<ProviderConnectionSummary[]>([])
  const [googleAccounts, setGoogleAccounts] = useState<ProviderAccountRecord[]>([])
  const [metaAccounts, setMetaAccounts] = useState<ProviderAccountRecord[]>([])
  const [error, setError] = useState<string | null>(null)
  const [rediscoveringConnectionId, setRediscoveringConnectionId] = useState<string | null>(null)
  const [deletingConnectionId, setDeletingConnectionId] = useState<string | null>(null)

  const loadAccountAccess = useCallback(async () => {
    const [connectionsValue, googleAccountsValue, metaAccountsValue] = await Promise.all([
      apiGet<ProviderConnectionSummary[]>("/api/provider-connections"),
      apiGet<ProviderAccountRecord[]>("/api/provider-accounts/google"),
      apiGet<ProviderAccountRecord[]>("/api/provider-accounts/meta"),
    ])
    setConnections(connectionsValue)
    setGoogleAccounts(googleAccountsValue)
    setMetaAccounts(metaAccountsValue)
  }, [])

  useEffect(() => {
    let cancelled = false

    void loadAccountAccess().catch((reason: unknown) => {
      if (!cancelled) {
        setError(reason instanceof Error ? reason.message : "Failed to load provider connections.")
      }
    })

    return () => {
      cancelled = true
    }
  }, [loadAccountAccess])

  async function rediscoverConnection(providerConnectionId: string) {
    setRediscoveringConnectionId(providerConnectionId)
    setError(null)
    try {
      const result = await apiPost<{
        accounts: ProviderAccountRecord[]
        failures: Array<{ source: string; message: string }>
      }>(`/api/provider-connections/${providerConnectionId}/rediscover`)
      setGoogleAccounts(result.accounts)
      setConnections(await apiGet<ProviderConnectionSummary[]>("/api/provider-connections"))
      if (result.failures.length > 0) {
        setError("Google account re-discovery completed with partial provider errors.")
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Google account re-discovery failed.")
    } finally {
      setRediscoveringConnectionId(null)
    }
  }

  async function deleteConnection(connection: ProviderConnectionSummary) {
    if (
      !window.confirm(`Disconnect ${labelForProvider(connection.provider)} from this workspace?`)
    ) {
      return
    }

    setDeletingConnectionId(connection.providerConnectionId)
    setError(null)
    try {
      await apiDelete<{ providerConnectionId: string }>(
        `/api/provider-connections/${connection.providerConnectionId}`
      )
      await loadAccountAccess()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to delete provider connection.")
    } finally {
      setDeletingConnectionId(null)
    }
  }

  return (
    <AuthenticatedAppShell eyebrow="Account access" title="Connected ad platforms">
      {() => (
        <>
          <PageToolbar
            actions={
              <>
                <Button component="a" href="/auth/connect/google?next=/app/accounts" size="xs">
                  Add Google connection
                </Button>
                <Button
                  component="a"
                  href="/auth/connect/meta?next=/app/accounts"
                  size="xs"
                  variant="subtle"
                >
                  Add Meta connection
                </Button>
              </>
            }
          >
            Connect the ad and analytics accounts Adgiro uses to build campaigns, read performance,
            and optimize spend.
          </PageToolbar>

          {error ? (
            <EmptyState>
              <strong>Account access unavailable</strong>
              <br />
              {error}
            </EmptyState>
          ) : null}

          <AppCard>
            <SectionHeader title="Platform connections" />
            <DataTableShell minWidth={900} mt="md">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Platform</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Available accounts</Table.Th>
                  <Table.Th>Linked sites</Table.Th>
                  <Table.Th>Last checked</Table.Th>
                  <Table.Th aria-label="Actions" />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {connections.map((connection) => {
                  const accounts = accountsForConnection(connection.providerConnectionId, [
                    ...googleAccounts,
                    ...metaAccounts,
                  ])
                  const rediscovering =
                    rediscoveringConnectionId === connection.providerConnectionId
                  const deleting = deletingConnectionId === connection.providerConnectionId

                  return (
                    <Fragment key={connection.providerConnectionId}>
                      <Table.Tr>
                        <Table.Td>{labelForProvider(connection.provider)}</Table.Td>
                        <Table.Td>
                          <span>{connectionStatus(connection)}</span>
                          {connection.provider === "google" ? (
                            <span className="muted-block">
                              {googleCapabilityLabel(connection.capabilities)}
                            </span>
                          ) : null}
                        </Table.Td>
                        <Table.Td>{connection.providerAccountCount}</Table.Td>
                        <Table.Td>{connection.linkedSiteCount}</Table.Td>
                        <Table.Td>
                          {formatDate(connection.lastVerifiedAt ?? connection.updatedAt)}
                        </Table.Td>
                        <Table.Td className="data-table__actions">
                          {connection.provider === "google" ? (
                            <ActionIcon
                              aria-label="Re-discover Google accounts"
                              variant="subtle"
                              onClick={() =>
                                void rediscoverConnection(connection.providerConnectionId)
                              }
                              disabled={rediscovering || deleting}
                              title="Re-discover Google accounts"
                            >
                              ↻
                            </ActionIcon>
                          ) : null}
                          <ActionIcon
                            aria-label={`Delete ${labelForProvider(connection.provider)} connection`}
                            color="red"
                            variant="subtle"
                            onClick={() => void deleteConnection(connection)}
                            disabled={rediscovering || deleting}
                            title={`Delete ${labelForProvider(connection.provider)} connection`}
                          >
                            ×
                          </ActionIcon>
                        </Table.Td>
                      </Table.Tr>
                      <Table.Tr className="connection-accounts-row">
                        <Table.Td colSpan={6}>
                          {accounts.length > 0 ? (
                            <div className="connection-accounts">
                              {accounts.map((account) => (
                                <div className="connection-account" key={account.providerAccountId}>
                                  <span>{account.label ?? account.externalAccountId}</span>
                                  <span>{formatAccountType(account.accountType)}</span>
                                  <span>{account.externalAccountId}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="muted-text">No discovered accounts yet.</span>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    </Fragment>
                  )
                })}
                {connections.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      Add a Google or Meta connection to start account discovery.
                    </Table.Td>
                  </Table.Tr>
                ) : null}
              </Table.Tbody>
            </DataTableShell>
          </AppCard>
        </>
      )}
    </AuthenticatedAppShell>
  )
}

function accountsForConnection(
  providerConnectionId: string,
  accounts: ProviderAccountRecord[]
): ProviderAccountRecord[] {
  return accounts.filter((account) => account.providerConnectionId === providerConnectionId)
}

function connectionStatus(connection: ProviderConnectionSummary): string {
  if (connection.status !== "active") {
    return "Needs reconnect"
  }

  if (connection.providerAccountCount === 0) {
    return "Connected, discovery needs attention"
  }

  return "Connected"
}

function formatDate(value: string | null): string {
  return formatLocalDateTime(value)
}

function googleCapabilityLabel(
  capabilities: ProviderConnectionSummary["capabilities"] | undefined
): string {
  const enabled = [
    capabilities?.canReadAds ? "Ads" : null,
    capabilities?.canManageAnalytics ? "Analytics" : null,
    capabilities?.canManageSearchConsole ? "Search Console" : null,
  ].filter(Boolean)
  return enabled.length > 0 ? enabled.join(", ") : "Reconnect to add management scopes"
}
